import React, {forwardRef, useEffect, useImperativeHandle} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {useTranslation} from 'react-i18next';
import {CodeJar} from 'codejar';
import Prism from 'prismjs';
import 'prismjs/themes/prism-dark.css';
import 'prismjs/components/prism-markdown';
import {marked} from 'marked';
import sanitizeHtml from 'sanitize-html';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {useTheme} from '@mui/material/styles';
import AgyleButton from '../ui/buttons/agyleButton';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';

const useStyles = makeStyles((theme) => ({
    root: {},
    editor: {
        position: 'relative',
        padding: 10,
    },
    previewRoot: {},
    preview: {
        border: '1px solid ' + theme.palette.divider,
        padding: 10,
    },
    labelFake: {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        position: 'absolute',
        fontSize: '90%',
        border: '1px solid ' + theme.palette.divider,
        '& .txt': {
            color: 'transparent',
            position: 'absolute',
            transform: 'translate(10px, -1px)',
            padding: '0 5px',
        },
        '& .txtUp': {
            color: 'transparent',
            position: 'absolute',
            transform: 'translate(10px, -1px)',
            padding: '0 5px',
        },
    },
    label: {
        top: 5,
        left: 0,
        right: 0,
        bottom: 0,
        position: 'absolute',
        transform: 'translate(0, -8px)',
        fontSize: '90%',
        opacity: .8,
        '& .txt': {
            position: 'absolute',
            padding: '0 5px',
            transform: 'translate(11px, -1px)',
            backgroundColor: 'transparent !important',
        },
        '& .txtUp': {
            position: 'absolute',
            padding: '0 5px',
            transform: 'translate(11px, -7px)',
        },
    },
    previewToggle: {
        fontSize: '90%',
        margin: '5px 0',
    },
}));

function EditorMarkdownComponent(props, ref) {
    const classes = useStyles();
    const {t} = useTranslation();
    const theme = useTheme();

    const editorRef = React.useRef(null);
    const jarRef = React.useRef(null);

    const [markdown, setMarkdown] = React.useState(null);
    const [initialized, setInitialized] = React.useState(false);

    const [previewVisible, setPreviewVisible] = React.useState(false);

    const highlight = editor => {
        const text = editor.textContent;
        editor.innerHTML = Prism.highlight(
            text,
            Prism.languages.markdown,
            'markdown',
        );
    };

    useImperativeHandle(ref, () => ({
        updateValue: (value) => {
            if (jarRef?.current) {
                jarRef.current.updateCode(value);
            }
        },
    }));

    useEffect(() => {
        if (!jarRef?.current) return;

        const textChanged = txt => {
            if (!jarRef?.current) return;

            let html = marked(sanitizeHtml(txt), {
                breaks: true,
            });
            setMarkdown(html);

            if (props.onChange) {
                props.onChange({
                    target: {
                        value: txt,
                    },
                });
            }
        };

        jarRef.current.onUpdate(textChanged);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.onChange, jarRef, initialized]);

    useEffect(() => {
        if (editorRef?.current) {
            let jar = CodeJar(editorRef.current, highlight, {
                spellcheck: true,
                addClosing: false,
            });
            jarRef.current = jar;

            jar.updateCode(props.code);
            if (props.onChange) {
                props.onChange({
                    target: {
                        value: props.code,
                    },
                });
            }
            setInitialized(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorRef, props.updateVal]);

    useEffect(() => {
        if (props.previewStateChange) {
            props.previewStateChange(previewVisible);
        }
    }, [previewVisible]);

    return (
        <div
            className={classes.root}
            style={{pointerEvents: props.disabled ? 'none' : 'unset', opacity: props.disabled ? '0.5' : '1'}}
            onClick={() => {
                if (editorRef?.current && !props.disabled) {
                    editorRef.current.focus();
                }
            }}
        >
            <div
                className={classes.editor}
                style={{
                    backgroundColor: props.ignorePaper ? theme.palette.background.default : theme.palette.background.paper,
                    height: props.height,
                }}
            >
                <div className={classes.labelFake}>
                    <span
                        className={props.labelClass ? props.labelClass : 'txt'}
                        style={{backgroundColor: props.ignorePaper ? theme.palette.background.default : theme.palette.background.paper}}
                    >
                        {props.label}
                    </span>
                </div>

                <div className={classes.label}>
                    <span
                        className={props.labelClass ? props.labelClass : 'txt'}
                        style={{backgroundColor: props.ignorePaper ? theme.palette.background.default : theme.palette.background.paper}}
                    >
                        {props.label}
                    </span>
                </div>

                <PerfectScrollbar>
                    <div ref={editorRef}/>
                </PerfectScrollbar>
            </div>

            {!props.hidePreview &&
                <div className={classes.previewRoot}>
                    <AgyleButton
                        onClick={() => setPreviewVisible(!previewVisible)}
                        size="small"
                        colour="primary"
                        className={classes.previewToggle}
                        startIcon={<RemoveRedEyeOutlinedIcon/>}
                    >
                        {previewVisible ? t('Hide preview') : t('Show preview')}
                    </AgyleButton>

                    {previewVisible &&
                        <div
                            className={classes.preview}
                            style={{
                                backgroundColor: props.ignorePaper ? theme.palette.background.default : theme.palette.background.paper,
                                height: props.height,
                            }}
                        >
                            <PerfectScrollbar>
                                <div dangerouslySetInnerHTML={{__html: markdown}}/>
                            </PerfectScrollbar>
                        </div>
                    }
                </div>
            }
        </div>
    );
}

// eslint-disable-next-line no-func-assign
EditorMarkdownComponent = forwardRef(EditorMarkdownComponent);

export default EditorMarkdownComponent;
